import { useMutation } from '@apollo/client';
import React from 'react';
import styled from 'styled-components';

import { AsyncTextButton } from 'components/lib/ui/AsyncTextButton';
import FlexContainer from 'components/lib/ui/FlexContainer';
import Text from 'components/lib/ui/Text';

import { RESEND_REFERRAL_REWARD_EMAIL } from 'common/lib/graphQl/referrals';
import useLoading from 'common/lib/hooks/useLoading';
import { spacing, variables } from 'common/lib/theme/dynamic';
import { formatCurrencyNoCents } from 'common/utils/Currency';
import { isoDateToAbbreviatedMonthDayAndYear } from 'common/utils/date';
import useToast from 'lib/hooks/useToast';

import { REFERRALS } from 'common/constants/copy';

import type {
  ReferralEarningsRedemptionsFieldsFragment,
  Common_ResendReferralRewardEmailMutation,
} from 'common/generated/graphql';

const EarningsItem = styled(FlexContainer).attrs({ justifyBetween: true })`
  padding: ${spacing.small} 0;
  gap: ${spacing.xlarge};
  border-top: 1px solid ${variables.color.divider.primary};
`;

type Props = {
  redemption: ReferralEarningsRedemptionsFieldsFragment;
};

export const ReferralRedemption = ({ redemption }: Props) => {
  const { id, creditsEarned, creditsEarnedAt } = redemption;
  const [resendReferralRewardEmail] = useMutation<Common_ResendReferralRewardEmailMutation>(
    RESEND_REFERRAL_REWARD_EMAIL,
  );
  const { openToast, openErrorToast } = useToast();

  const [isResendingEmail, onResendEmail] = useLoading(() =>
    resendReferralRewardEmail({
      variables: { referralRedemptionId: id },
      onCompleted: (data) => {
        if (data.resendReferralRewardEmail?.success) {
          openToast({
            title: REFERRALS.RESEND_REWARD_EMAIL.TITLE,
            description: REFERRALS.RESEND_REWARD_EMAIL.DESCRIPTION,
          });
        } else {
          openErrorToast();
        }
      },
      onError: () => openErrorToast(),
    }),
  );

  return (
    <EarningsItem>
      <Text color="content.primary">
        {creditsEarnedAt && `${isoDateToAbbreviatedMonthDayAndYear(creditsEarnedAt)} - `}
        {formatCurrencyNoCents(creditsEarned)} earned
      </Text>

      <AsyncTextButton isLoading={isResendingEmail} onClick={onResendEmail}>
        Resend email
      </AsyncTextButton>
    </EarningsItem>
  );
};
